import Button from '@components/ui/button'
import OutlineButton from '@components/ui/outline-button'
import { useCart } from '@contexts/cart/cart.context'
import { Item } from '@contexts/cart/cart.utils'
import { getProductsBySkus } from '@framework/product/get-product'
import saveListService from '@services/savelistservice'
import { AxiosResponse } from 'axios'
import { useRouter } from 'next/router'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import ErrorMessage from './quick-order-error'
import { ROUTES } from '@utils/routes'

interface CreateListOrderProps {
  savedLists: (AxiosResponse<any, any> & any[]) | AxiosResponse<any, any>[]
  isSavedListsLoading
}

const CreateListOrder: FC<CreateListOrderProps> = ({
  savedLists,
  isSavedListsLoading,
}) => {
  const [selectedList, setSelectedList] = useState<string>('')
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [errorSkus, setErrorSkus] = useState<string | null>(null)
  const [isOrderLoading, setIsOrderLoading] = useState(false)
  const [onlyError, setOnlyError] = useState(false)
  const { addOrUpdateItemsHandler } = useCart()
  const { locale, push } = useRouter()
  const [dropdownError, setDropdownError] = useState<string | null>(null)
  const { t: translate } = useTranslation(['quickorder', 'savelist'])

  // Handle dropdown change
  const handleListChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedIndex = event.target.selectedIndex - 1
    setSelectedList(event.target.value)
    setSelectedIndex(selectedIndex)
    setErrorSkus(null)
    setDropdownError(null) // Clear error if selection is valid
  }

  const getTypesenseProducts = async (selectedListItems) => {
    const skusString = selectedListItems
      .map((item: { sku: string }) => item.sku)
      .join(',')

    let selectedListTypesenseItems = await getProductsBySkus(
      skusString,
      selectedListItems,
      locale,
    )
    return selectedListTypesenseItems
  }
  const { mutate: createOrder, isLoading } = useMutation(
    ({
      payload,
      selectedListItems,
    }: {
      payload: { listId: string }
      selectedListItems: any[]
    }) => {
      return saveListService.createOrderBySavedList(payload)
    },
    {
      onSuccess: async (
        response: any,
        { selectedListItems }: { selectedListItems: Item[] },
      ) => {
        if (response?.errorSkus) setErrorSkus(response?.errorSkus)
        if (response?.status) {
          toast.success(response?.message)
          setOnlyError(false)
        } else {
          toast.error(response?.message, {
            position: toast.POSITION.TOP_RIGHT,
            className: 'toast-error',
          })
          setOnlyError(true)
          return
        }
        if (response?.errorSkusList?.length) {
          let updatedSelectedListItems = await selectedListItems.filter(
            (selectedItem) =>
              response.errorSkusList.some(
                (errorItem) => errorItem.sku !== selectedItem.sku,
              ),
          )
          updatedSelectedListItems = updatedSelectedListItems.length
            ? updatedSelectedListItems
            : selectedListItems
          addOrUpdateItemsHandler(updatedSelectedListItems)
        } else {
          addOrUpdateItemsHandler(selectedListItems)
        }
      },
      onError: (err: Error) => {
        toast.error(`Error list updation: ${err?.message}`, {
          position: toast.POSITION.TOP_RIGHT,
          className: 'toast-error',
        })
      },
    },
  )

  const createOrderHandler = async () => {
    if (!selectedList) {
      setDropdownError(translate('text-select-list-error'))
      return
    }
    setDropdownError(null) // Clear error if selection is valid

    if (selectedList && savedLists[selectedIndex]?.product_skus) {
      setIsOrderLoading(true)
      try {
        const items = await getTypesenseProducts(
          savedLists[selectedIndex]?.product_skus,
        )
        setIsOrderLoading(false)
        createOrder({
          payload: { listId: selectedList },
          selectedListItems: items, // Ensure this matches the expected type
        })
      } catch (err) {
        console.error(
          'Error fetching typesense products or creating order:',
          err,
        )
        toast.error(err)
        setIsOrderLoading(false) // Ensure loading state is reset on error
      }
    }
  }

  const redirecttoHome = () => push(ROUTES.HOME)

  return (
    <>
      {errorSkus ? (
        <ErrorMessage reportLink={errorSkus} onlyError={onlyError} />
      ) : null}

      <div className="p-4 sm:p-8 border-b border-gray-200 rounded px-8 mb-8">
        <p className="text-gray-600 text-sm mb-4">
          {translate('text-lists-description')}
        </p>
        {isSavedListsLoading ? (
          <p className="text-center">Loading....</p>
        ) : savedLists?.length ? (
          <div className="sm:w-1/2 mx-auto py-5 ">
            <label
              htmlFor="list"
              className="block mb-2 text-sm font-semibold text-brand-dark"
            >
              {translate('text-choose-from-list')}
            </label>
            <select
              id="list"
              value={selectedList}
              onChange={handleListChange}
              className={`block w-full px-3 py-2 sm:py-3 cursor-pointer border ${
                dropdownError ? 'border-brand-danger' : 'border-gray-300'
              } bg-gray-100 rounded focus:outline-none  sm:text-sm`}
            >
              <option value="">{translate('text-choose-list')}</option>
              {savedLists?.map((list) => (
                <option key={list.list_id} value={list.list_id}>
                  {list.list_name}
                </option>
              ))}
            </select>
            {dropdownError && (
              <p className="text-brand-danger text-sm mt-2">{dropdownError}</p>
            )}
          </div>
        ) : (
          <p className="text-center">
            {translate('text-no-list-found', { ns: 'savelist' })}
          </p>
        )}
      </div>
      <div className="flex justify-end p-4">
        <OutlineButton
          className="!border-brand rounded text-xs md:text-sm w-20 md:w-36 h-10 border-solid border-[1px] mr-1"
          data-testid="quickorder-actions-section-button-cancel-order"
          onClick={redirecttoHome}
        >
          {translate('text-cancel')}
        </OutlineButton>
        <Button
          className="text-xs md:text-sm  md:w-36 !h-[41px] "
          onClick={createOrderHandler}
          data-testid="quickorder-actions-section-button-create-order"
          loading={isLoading || isOrderLoading}
          disabled={!savedLists?.length}
        >
          {translate('text-create')}
        </Button>
      </div>
    </>
  )
}

export default CreateListOrder
